import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { objectFitContainRect } from './utils/objectFitContainRect';
import ExploreUnknownMinigameMapMarker from './exploreUnknownMinigames/ExploreUnknownMinigameMapMarker';
import { getExploreUnknownMissionOpponentFactionName } from './config/exploreUnknownMissionOpponentFactions';
import {
  getExploreUnknownMapFactionIconPath,
  getExploreUnknownMapFactionOrbBackground,
} from './utils/exploreUnknownMapFactionDisplay';

/** Must match `exploreUnknownPortalLayout.js` POI authoring (logical design canvas). */
const DEFAULT_AUTHORING_WIDTH = 1920;
const DEFAULT_AUTHORING_HEIGHT = 1080;

/**
 * Convert pointer client coordinates to authoring canvas px (1920×1080 design space).
 */
function clientToAuthoringPoint(
  svgEl,
  clientX,
  clientY,
  {
    useNaturalAlignment,
    logicalWidth,
    logicalHeight,
    naturalWidth,
    naturalHeight,
    authoringWidth,
    authoringHeight,
  }
) {
  if (!svgEl) {
    throw new Error('ExploreUnknownArrivalPointsOverlay: SVG element is required for coordinate conversion');
  }
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl.getScreenCTM();
  if (!ctm) {
    throw new Error('ExploreUnknownArrivalPointsOverlay: getScreenCTM() returned null');
  }
  const p = pt.matrixTransform(ctm.inverse());
  const nx = p.x;
  const ny = p.y;

  if (useNaturalAlignment) {
    const clampedX = Math.max(0, Math.min(naturalWidth, nx));
    const clampedY = Math.max(0, Math.min(naturalHeight, ny));
    return [
      (clampedX / naturalWidth) * authoringWidth,
      (clampedY / naturalHeight) * authoringHeight,
    ];
  }

  const clampedX = Math.max(0, Math.min(logicalWidth, nx));
  const clampedY = Math.max(0, Math.min(logicalHeight, ny));
  return [
    (clampedX / logicalWidth) * authoringWidth,
    (clampedY / logicalHeight) * authoringHeight,
  ];
}

/**
 * Points of interest on shadowverse arrival: path + per-mission nodes.
 * States: available (clickable), done, unavailable.
 *
 * When `naturalWidth` + `naturalHeight` are set (from the loaded map `<img>`), the overlay
 * matches CSS `object-fit: contain`: same sub-rect as the bitmap, `viewBox` in image pixels.
 */
function ExploreUnknownArrivalPointsOverlay({
  logicalWidth,
  logicalHeight,
  points,
  pointStates,
  onAvailableMissionClick,
  /** Global Explore Unknown mission index for points[0] (1 = first mission on arrival map, 6 = first on cave). */
  firstMissionIndex = 1,
  zIndex = 13100,
  pathClassName = '',
  /** POI coordinates in layout file are authored vs this canvas (default 1920×1080). */
  authoringWidth = DEFAULT_AUTHORING_WIDTH,
  authoringHeight = DEFAULT_AUTHORING_HEIGHT,
  /** From loaded image `naturalWidth` / `naturalHeight`; when set, POIs align with `object-fit: contain`. */
  naturalWidth,
  naturalHeight,
  /** Development: drag handles to tune positions (authoring px reported upstream). */
  waypointEditMode = false,
  /** True when any route branch is in dev drag-edit mode (enables SVG pointer events). */
  layoutAuthoringActive = false,
  onWaypointAuthoringChange,
  /** Optional POI: Rite of respite (authoring px). Placed beside the unlocking mission. */
  darknessBargainAuthoringPoint = null,
  darknessBargainVisible = false,
  /** Insert into the path after this mission node index (0-based): … → mission[i] → rite → mission[i+1] → … */
  darknessBargainPolylineInsertAfterIndex = null,
  darknessBargainInteractive = false,
  onDarknessBargainClick,
  /** Dev: extra rite markers for alternate route layouts `{ routeKey, point, stroke }[]`. */
  additionalDarknessBargainMarkers = null,
  /** Optional map minigame marker (authoring px, random per run). */
  minigameAuthoringPoint = null,
  minigameVisible = false,
  minigameInteractive = false,
  onMinigameClick,
  /** Global mission indices to render as boss waypoints (red warning triangle + DANGER label). */
  bossMissionIndices = null,
  /** Optional explicit global mission index per POI (overrides firstMissionIndex + i). */
  missionIndicesPerPoint = null,
  /** Global mission indices flagged as easy (alternative route pacifier icon). */
  easyMissionIndices = null,
  /** Level bonus: easier bosses on alternative path — show pacifier on alt-route boss waypoints. */
  altPathEasierBossesEnabled = false,
  /** Main displayed path is the committed alternative route (boss pacifier on main boss nodes). */
  showAltBossPacifierOnMainRoute = false,
  /** Optional second path branch for alternative route UI. */
  alternativeBranch = null,
  /** Optional extra alternative branches (dev: alt −2 alongside alt −1). */
  additionalAlternativeBranches = null,
  /** Passed to main-route mission clicks (e.g. `{ routeBranch: 'normal' }`). */
  mainRouteMissionClickOptions = null,
}) {
  const svgRef = useRef(null);
  const [draggingIndex, setDraggingIndex] = useState(null);

  if (
    typeof authoringWidth !== 'number' ||
    typeof authoringHeight !== 'number' ||
    authoringWidth <= 0 ||
    authoringHeight <= 0
  ) {
    throw new Error('ExploreUnknownArrivalPointsOverlay: authoringWidth/authoringHeight must be positive numbers');
  }

  if (
    typeof firstMissionIndex !== 'number' ||
    !Number.isInteger(firstMissionIndex) ||
    firstMissionIndex < 1
  ) {
    throw new Error(`ExploreUnknownArrivalPointsOverlay: invalid firstMissionIndex (${firstMissionIndex})`);
  }

  if (waypointEditMode && typeof onWaypointAuthoringChange !== 'function') {
    throw new Error(
      'ExploreUnknownArrivalPointsOverlay: onWaypointAuthoringChange is required when waypointEditMode is true'
    );
  }

  const useNaturalAlignment =
    typeof naturalWidth === 'number' &&
    typeof naturalHeight === 'number' &&
    naturalWidth > 0 &&
    naturalHeight > 0;

  const { svgPoints, svgStyle, viewBox, preserveAspectRatio } = useMemo(() => {
    if (!Array.isArray(points) || points.length === 0) {
      return {
        svgPoints: [],
        svgStyle: {
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          zIndex,
          pointerEvents: 'none',
        },
        viewBox: `0 0 ${logicalWidth} ${logicalHeight}`,
        preserveAspectRatio: 'none',
      };
    }
    if (useNaturalAlignment) {
      const rect = objectFitContainRect(logicalWidth, logicalHeight, naturalWidth, naturalHeight);
      const mapped = points.map(([px, py]) => [
        (px / authoringWidth) * naturalWidth,
        (py / authoringHeight) * naturalHeight,
      ]);
      return {
        svgPoints: mapped,
        svgStyle: {
          position: 'absolute',
          left: rect.x,
          top: rect.y,
          width: rect.width,
          height: rect.height,
          zIndex,
          pointerEvents: waypointEditMode || layoutAuthoringActive ? 'auto' : 'none',
        },
        viewBox: `0 0 ${naturalWidth} ${naturalHeight}`,
        preserveAspectRatio: 'xMidYMid meet',
      };
    }
    return {
      svgPoints: points,
      svgStyle: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex,
        pointerEvents: waypointEditMode || layoutAuthoringActive ? 'auto' : 'none',
      },
      viewBox: `0 0 ${logicalWidth} ${logicalHeight}`,
      preserveAspectRatio: 'none',
    };
  }, [
    useNaturalAlignment,
    logicalWidth,
    logicalHeight,
    naturalWidth,
    naturalHeight,
    authoringWidth,
    authoringHeight,
    points,
    zIndex,
    waypointEditMode,
    layoutAuthoringActive,
  ]);

  const mapAuthoringPointToSvg = useCallback(
    (authoringPoint) => {
      if (!Array.isArray(authoringPoint) || authoringPoint.length !== 2) return null;
      const [bx, by] = authoringPoint;
      if (typeof bx !== 'number' || typeof by !== 'number' || Number.isNaN(bx) || Number.isNaN(by)) {
        return null;
      }
      if (useNaturalAlignment) {
        return [
          (bx / authoringWidth) * naturalWidth,
          (by / authoringHeight) * naturalHeight,
        ];
      }
      return [bx, by];
    },
    [useNaturalAlignment, authoringWidth, authoringHeight, naturalWidth, naturalHeight]
  );

  const additionalDarknessBargainSvgMarkers = useMemo(() => {
    if (!Array.isArray(additionalDarknessBargainMarkers) || additionalDarknessBargainMarkers.length === 0) {
      return [];
    }
    return additionalDarknessBargainMarkers
      .map((marker) => {
        const svgPoint = mapAuthoringPointToSvg(marker.point);
        if (svgPoint == null) return null;
        return { ...marker, svgPoint };
      })
      .filter(Boolean);
  }, [additionalDarknessBargainMarkers, mapAuthoringPointToSvg]);

  const darknessBargainSvgPoint = useMemo(() => {
    if (!darknessBargainVisible || !Array.isArray(darknessBargainAuthoringPoint) || darknessBargainAuthoringPoint.length !== 2) {
      return null;
    }
    return mapAuthoringPointToSvg(darknessBargainAuthoringPoint);
  }, [darknessBargainVisible, darknessBargainAuthoringPoint, mapAuthoringPointToSvg]);

  const minigameSvgPoint = useMemo(() => {
    if (!minigameVisible || !Array.isArray(minigameAuthoringPoint) || minigameAuthoringPoint.length !== 2) {
      return null;
    }
    const [mx, my] = minigameAuthoringPoint;
    if (typeof mx !== 'number' || typeof my !== 'number' || Number.isNaN(mx) || Number.isNaN(my)) {
      throw new Error('ExploreUnknownArrivalPointsOverlay: minigameAuthoringPoint must be numeric [x, y]');
    }
    if (useNaturalAlignment) {
      return [
        (mx / authoringWidth) * naturalWidth,
        (my / authoringHeight) * naturalHeight,
      ];
    }
    return [mx, my];
  }, [
    minigameVisible,
    minigameAuthoringPoint,
    useNaturalAlignment,
    authoringWidth,
    authoringHeight,
    naturalWidth,
    naturalHeight,
  ]);

  const polylineSvgPoints = useMemo(() => {
    const routePoints = svgPoints;
    if (
      !darknessBargainVisible ||
      darknessBargainSvgPoint == null ||
      darknessBargainPolylineInsertAfterIndex == null ||
      !Number.isInteger(darknessBargainPolylineInsertAfterIndex)
    ) {
      return routePoints;
    }
    const i = darknessBargainPolylineInsertAfterIndex;
    if (i < 0 || i >= routePoints.length) {
      throw new Error(
        `ExploreUnknownArrivalPointsOverlay: darknessBargainPolylineInsertAfterIndex out of range (${i} vs ${routePoints.length})`
      );
    }
    return [...routePoints.slice(0, i + 1), darknessBargainSvgPoint, ...routePoints.slice(i + 1)];
  }, [
    svgPoints,
    darknessBargainVisible,
    darknessBargainSvgPoint,
    darknessBargainPolylineInsertAfterIndex,
  ]);

  const emitAuthoringFromClient = useCallback(
    (clientX, clientY, index) => {
      const [ax, ay] = clientToAuthoringPoint(svgRef.current, clientX, clientY, {
        useNaturalAlignment,
        logicalWidth,
        logicalHeight,
        naturalWidth: naturalWidth ?? 0,
        naturalHeight: naturalHeight ?? 0,
        authoringWidth,
        authoringHeight,
      });
      onWaypointAuthoringChange(index, ax, ay);
    },
    [
      useNaturalAlignment,
      logicalWidth,
      logicalHeight,
      naturalWidth,
      naturalHeight,
      authoringWidth,
      authoringHeight,
      onWaypointAuthoringChange,
    ]
  );

  useEffect(() => {
    if (!waypointEditMode || draggingIndex === null) return undefined;

    const onMove = (e) => {
      emitAuthoringFromClient(e.clientX, e.clientY, draggingIndex);
    };
    const onUp = () => {
      setDraggingIndex(null);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [waypointEditMode, draggingIndex, emitAuthoringFromClient]);

  const easyMissionIndexSet = useMemo(() => {
    if (easyMissionIndices == null) return null;
    if (!Array.isArray(easyMissionIndices)) {
      throw new Error('ExploreUnknownArrivalPointsOverlay: easyMissionIndices must be an array');
    }
    return new Set(easyMissionIndices.map(Number));
  }, [easyMissionIndices]);

  const bossMissionIndexSet = useMemo(() => {
    if (bossMissionIndices == null) return null;
    if (!Array.isArray(bossMissionIndices)) {
      throw new Error('ExploreUnknownArrivalPointsOverlay: bossMissionIndices must be an array');
    }
    const set = new Set();
    for (const idx of bossMissionIndices) {
      if (!Number.isInteger(idx) || idx < 1) {
        throw new Error(
          `ExploreUnknownArrivalPointsOverlay: bossMissionIndices entries must be positive integers (got ${idx})`
        );
      }
      set.add(idx);
    }
    return set;
  }, [bossMissionIndices]);

  if (!Array.isArray(points) || points.length === 0) {
    return null;
  }

  if (!Array.isArray(pointStates) || pointStates.length !== points.length) {
    throw new Error(
      `ExploreUnknownArrivalPointsOverlay: pointStates must match points length (got ${pointStates?.length} vs ${points.length})`
    );
  }

  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    if (!Array.isArray(p) || p.length !== 2) {
      throw new Error(
        `ExploreUnknownArrivalPointsOverlay: point ${i} must be [x, y], got ${JSON.stringify(p)}`
      );
    }
    const [x, y] = p;
    if (typeof x !== 'number' || typeof y !== 'number' || Number.isNaN(x) || Number.isNaN(y)) {
      throw new Error(`ExploreUnknownArrivalPointsOverlay: point ${i} must be numeric [x, y]`);
    }
    const st = pointStates[i];
    if (st != null && st !== 'available' && st !== 'done' && st !== 'unavailable') {
      throw new Error(`ExploreUnknownArrivalPointsOverlay: invalid state "${st}" at index ${i}`);
    }
  }

  const polylinePoints = polylineSvgPoints.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <svg
      ref={svgRef}
      className={`explore-unknown-arrival-poi-overlay ${pathClassName}`.trim()}
      style={svgStyle}
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      aria-hidden
    >
      <polyline
        points={polylinePoints}
        fill="none"
        stroke="rgba(0, 255, 200, 0.45)"
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        style={waypointEditMode ? { pointerEvents: 'none' } : undefined}
      />
      {darknessBargainSvgPoint ? (
        <DarknessBargainMapMarker
          x={darknessBargainSvgPoint[0]}
          y={darknessBargainSvgPoint[1]}
          interactive={darknessBargainInteractive}
          waypointEditMode={waypointEditMode}
          onDarknessBargainClick={onDarknessBargainClick}
        />
      ) : null}
      {additionalDarknessBargainSvgMarkers.map((marker) => (
        <DarknessBargainMapMarker
          key={`darkness-bargain-dev-${marker.routeKey}`}
          x={marker.svgPoint[0]}
          y={marker.svgPoint[1]}
          interactive={false}
          waypointEditMode={waypointEditMode}
          stroke={marker.stroke}
        />
      ))}
      {minigameSvgPoint ? (
        <ExploreUnknownMinigameMapMarker
          x={minigameSvgPoint[0]}
          y={minigameSvgPoint[1]}
          interactive={minigameInteractive}
          onClick={onMinigameClick}
          waypointEditMode={waypointEditMode}
        />
      ) : null}
      {alternativeBranch ? (
        <AlternativePathBranchOverlay
          branch={alternativeBranch}
          bossMissionIndexSet={bossMissionIndexSet}
          altPathEasierBossesEnabled={altPathEasierBossesEnabled}
          waypointEditMode={waypointEditMode}
          onAvailableMissionClick={onAvailableMissionClick}
          useNaturalAlignment={useNaturalAlignment}
          logicalWidth={logicalWidth}
          logicalHeight={logicalHeight}
          naturalWidth={naturalWidth}
          naturalHeight={naturalHeight}
          authoringWidth={authoringWidth}
          authoringHeight={authoringHeight}
          svgRef={svgRef}
        />
      ) : null}
      {Array.isArray(additionalAlternativeBranches)
        ? additionalAlternativeBranches.map((branch, branchIndex) => (
            <AlternativePathBranchOverlay
              key={`alt-branch-extra-${branchIndex}`}
              branch={branch}
              bossMissionIndexSet={bossMissionIndexSet}
              altPathEasierBossesEnabled={altPathEasierBossesEnabled}
              waypointEditMode={waypointEditMode}
              onAvailableMissionClick={onAvailableMissionClick}
              useNaturalAlignment={useNaturalAlignment}
              logicalWidth={logicalWidth}
              logicalHeight={logicalHeight}
              naturalWidth={naturalWidth}
              naturalHeight={naturalHeight}
              authoringWidth={authoringWidth}
              authoringHeight={authoringHeight}
              svgRef={svgRef}
            />
          ))
        : null}
      {svgPoints.map(([x, y], i) => {
        const state = pointStates[i];
        const missionIndex =
          Array.isArray(missionIndicesPerPoint) && missionIndicesPerPoint[i] != null
            ? missionIndicesPerPoint[i]
            : firstMissionIndex + i;
        if (state == null) {
          return null;
        }
        const isBoss = bossMissionIndexSet != null && bossMissionIndexSet.has(missionIndex);
        const isEasy = easyMissionIndexSet != null && easyMissionIndexSet.has(missionIndex);
        const showBossPacifier =
          isBoss && altPathEasierBossesEnabled && showAltBossPacifierOnMainRoute;
        if (isBoss) {
          return (
            <BossMissionWaypoint
              key={i}
              x={x}
              y={y}
              state={state}
              missionIndex={missionIndex}
              waypointEditMode={waypointEditMode}
              onAvailableMissionClick={onAvailableMissionClick}
              missionClickOptions={mainRouteMissionClickOptions}
              showPacifier={showBossPacifier}
            />
          );
        }
        const factionName = getExploreUnknownMissionOpponentFactionName(missionIndex);
        return (
          <FactionMissionWaypoint
            key={i}
            x={x}
            y={y}
            state={state}
            missionIndex={missionIndex}
            factionName={factionName}
            isEasyMission={isEasy}
            waypointEditMode={waypointEditMode}
            onAvailableMissionClick={onAvailableMissionClick}
            missionClickOptions={mainRouteMissionClickOptions}
          />
        );
      })}
      {waypointEditMode
        ? svgPoints.map(([x, y], i) => (
            <circle
              key={`dev-handle-${i}`}
              className="explore-unknown-poi-dev-handle"
              cx={x}
              cy={y}
              r="24"
              fill="rgba(255, 60, 255, 0.2)"
              stroke="rgba(255, 255, 255, 0.95)"
              strokeWidth="2"
              style={{
                cursor: draggingIndex === i ? 'grabbing' : 'grab',
                pointerEvents: 'auto',
                touchAction: 'none',
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
                  e.currentTarget.setPointerCapture(e.pointerId);
                }
                setDraggingIndex(i);
                emitAuthoringFromClient(e.clientX, e.clientY, i);
              }}
            />
          ))
        : null}
    </svg>
  );
}

function DarknessBargainMapMarker({
  x,
  y,
  interactive = false,
  waypointEditMode = false,
  onDarknessBargainClick,
  stroke = 'rgba(196, 181, 253, 0.95)',
}) {
  return (
    <g
      className="explore-unknown-darkness-bargain-poi"
      style={{
        pointerEvents: waypointEditMode || !interactive ? 'none' : 'auto',
        cursor: interactive && !waypointEditMode ? 'pointer' : 'default',
      }}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive && !waypointEditMode ? 0 : undefined}
      onClick={
        waypointEditMode || !interactive || typeof onDarknessBargainClick !== 'function'
          ? undefined
          : (e) => {
              onDarknessBargainClick();
              e.currentTarget.blur();
            }
      }
      onKeyDown={
        waypointEditMode || !interactive || typeof onDarknessBargainClick !== 'function'
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onDarknessBargainClick();
              }
            }
      }
    >
      <circle cx={x} cy={y} r="32" fill="transparent" />
      <circle
        cx={x}
        cy={y}
        r="14"
        fill={interactive ? 'rgba(40, 20, 60, 0.92)' : 'rgba(40, 20, 60, 0.82)'}
        stroke={stroke}
        strokeWidth="3"
      />
      <text
        x={x}
        y={y + 5}
        textAnchor="middle"
        fill="rgba(230, 220, 255, 0.95)"
        fontSize="14"
        fontWeight="700"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        ☽
      </text>
    </g>
  );
}

const FACTION_MISSION_ORB_SIZE = 28;

/** Lower-left pacifier badge anchored to the faction-orb footprint at `(x, y)`. */
function MissionPacifierBadge({ x, y }) {
  const half = FACTION_MISSION_ORB_SIZE / 2;
  return (
    <foreignObject
      x={x - half}
      y={y - half}
      width={FACTION_MISSION_ORB_SIZE}
      height={FACTION_MISSION_ORB_SIZE}
      className="explore-unknown-poi-pacifier-host"
      aria-hidden
    >
      <div xmlns="http://www.w3.org/1999/xhtml" className="explore-unknown-poi-pacifier-anchor">
        <img
          src="/images/pacifier.png"
          alt=""
          className="explore-unknown-poi-mission-pacifier"
          draggable={false}
          aria-hidden="true"
        />
      </div>
    </foreignObject>
  );
}

/**
 * Regular mission waypoint: faction-colored orb with transparent-background faction icon.
 * Active (`available`) missions are full color; inactive (`unavailable`) are grayed out;
 * `done` keeps faction art with a green tick overlay.
 */
function AlternativePathBranchOverlay({
  branch,
  bossMissionIndexSet,
  altPathEasierBossesEnabled,
  waypointEditMode,
  onAvailableMissionClick,
  useNaturalAlignment,
  logicalWidth,
  logicalHeight,
  naturalWidth,
  naturalHeight,
  authoringWidth,
  authoringHeight,
  svgRef,
}) {
  const { t } = useTranslation();
  const [draggingFork, setDraggingFork] = useState(false);
  const [draggingBranchIndex, setDraggingBranchIndex] = useState(null);
  if (!branch || !Array.isArray(branch.points)) return null;
  const mapPoint = (pt) => {
    const [px, py] = pt;
    if (useNaturalAlignment) {
      return [(px / authoringWidth) * naturalWidth, (py / authoringHeight) * naturalHeight];
    }
    return [px, py];
  };
  const fork = mapPoint(branch.forkPoint);
  const altPts = branch.points.map(mapPoint);
  const sharedToFork = branch.sharedStartPoint ? mapPoint(branch.sharedStartPoint) : null;
  const missionIndices = branch.missionIndicesPerPoint || [];
  const branchAltPts = altPts.slice(1);
  const polyParts = [];
  if (sharedToFork) polyParts.push(`${sharedToFork[0]},${sharedToFork[1]}`);
  polyParts.push(`${fork[0]},${fork[1]}`);
  branchAltPts.forEach(([ax, ay]) => polyParts.push(`${ax},${ay}`));
  const locked = branch.locked === true;
  const branchStates = branch.pointStates || [];
  const previewFactions = branch.previewFactions !== false;
  const forkEditMode = branch.forkEditMode === true && waypointEditMode;
  const branchDevEdit = branch.devEditActive === true;
  const branchStroke =
    typeof branch.branchStroke === 'string'
      ? branch.branchStroke
      : locked
        ? 'rgba(140, 140, 150, 0.35)'
        : 'rgba(120, 200, 255, 0.45)';
  const missionClickOptions = branch.missionClickOptions || { routeBranch: 'alternative' };

  const branchEasyMissionIndexSet = useMemo(() => {
    if (!Array.isArray(branch.easyMissionIndices)) return null;
    return new Set(branch.easyMissionIndices.map(Number));
  }, [branch.easyMissionIndices]);

  const emitForkAuthoringFromClient = useCallback(
    (clientX, clientY) => {
      if (typeof branch.onForkAuthoringChange !== 'function') return;
      const [ax, ay] = clientToAuthoringPoint(svgRef?.current, clientX, clientY, {
        useNaturalAlignment,
        logicalWidth,
        logicalHeight,
        naturalWidth: naturalWidth ?? 0,
        naturalHeight: naturalHeight ?? 0,
        authoringWidth,
        authoringHeight,
      });
      branch.onForkAuthoringChange(ax, ay);
    },
    [
      branch,
      svgRef,
      useNaturalAlignment,
      logicalWidth,
      logicalHeight,
      naturalWidth,
      naturalHeight,
      authoringWidth,
      authoringHeight,
    ]
  );

  useEffect(() => {
    if (!forkEditMode || !draggingFork) return undefined;
    const onMove = (e) => {
      emitForkAuthoringFromClient(e.clientX, e.clientY);
    };
    const onUp = () => setDraggingFork(false);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [forkEditMode, draggingFork, emitForkAuthoringFromClient]);

  const emitBranchAuthoringFromClient = useCallback(
    (clientX, clientY, index) => {
      if (typeof branch.onWaypointAuthoringChange !== 'function') return;
      const [ax, ay] = clientToAuthoringPoint(svgRef?.current, clientX, clientY, {
        useNaturalAlignment,
        logicalWidth,
        logicalHeight,
        naturalWidth: naturalWidth ?? 0,
        naturalHeight: naturalHeight ?? 0,
        authoringWidth,
        authoringHeight,
      });
      branch.onWaypointAuthoringChange(index, ax, ay);
    },
    [
      branch,
      svgRef,
      useNaturalAlignment,
      logicalWidth,
      logicalHeight,
      naturalWidth,
      naturalHeight,
      authoringWidth,
      authoringHeight,
    ]
  );

  useEffect(() => {
    if (!branchDevEdit || draggingBranchIndex == null) return undefined;
    const onMove = (e) => {
      emitBranchAuthoringFromClient(e.clientX, e.clientY, draggingBranchIndex);
    };
    const onUp = () => setDraggingBranchIndex(null);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [branchDevEdit, draggingBranchIndex, emitBranchAuthoringFromClient]);

  return (
    <g className={`explore-unknown-alt-path-branch${locked ? ' explore-unknown-alt-path-branch--locked' : ''}`}>
      <polyline
        points={polyParts.join(' ')}
        fill="none"
        stroke={branchStroke}
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray={locked ? '8 6' : undefined}
      />
      {locked ? (
        <text
          x={fork[0]}
          y={fork[1] - 36}
          textAnchor="middle"
          fill="rgba(200, 200, 210, 0.9)"
          fontSize="13"
          fontWeight="600"
        >
          {t('exploreUnknownPage.alternativePath.lockedUntilLevel20')}
        </text>
      ) : null}
      {altPts.map(([x, y], i) => {
        if (i === 0) return null;
        const state = branchStates[i] || 'unavailable';
        const missionIndex = missionIndices[i];
        const isBoss = bossMissionIndexSet != null && bossMissionIndexSet.has(missionIndex);
        const isEasy =
          branchEasyMissionIndexSet != null && branchEasyMissionIndexSet.has(missionIndex);
        const showBossPacifier = isBoss && altPathEasierBossesEnabled === true;
        if (branch.active !== true && previewFactions) {
          const previewState = locked ? 'unavailable' : state;
          if (isBoss) {
            return (
              <BossMissionWaypoint
                key={`alt-preview-${i}`}
                x={x}
                y={y}
                state={previewState}
                missionIndex={missionIndex}
                waypointEditMode={waypointEditMode || branchDevEdit}
                onAvailableMissionClick={onAvailableMissionClick}
                missionClickOptions={missionClickOptions}
                showPacifier={showBossPacifier}
              />
            );
          }
          const factionName = getExploreUnknownMissionOpponentFactionName(missionIndex);
          return (
            <FactionMissionWaypoint
              key={`alt-preview-${i}`}
              x={x}
              y={y}
              state={previewState}
              missionIndex={missionIndex}
              factionName={factionName}
              isEasyMission={isEasy}
              waypointEditMode={waypointEditMode || branchDevEdit}
              onAvailableMissionClick={onAvailableMissionClick}
              missionClickOptions={missionClickOptions}
            />
          );
        }
        if (branch.active !== true) {
          return (
            <circle
              key={`alt-preview-${i}`}
              cx={x}
              cy={y}
              r="14"
              fill="rgba(80, 80, 90, 0.35)"
              stroke="rgba(160, 160, 170, 0.5)"
              strokeWidth="2"
            />
          );
        }
        if (isBoss) {
          return (
            <BossMissionWaypoint
              key={`alt-${i}`}
              x={x}
              y={y}
              state={state}
              missionIndex={missionIndex}
              waypointEditMode={waypointEditMode || branchDevEdit}
              onAvailableMissionClick={onAvailableMissionClick}
              missionClickOptions={missionClickOptions}
              showPacifier={showBossPacifier}
            />
          );
        }
        const factionName = getExploreUnknownMissionOpponentFactionName(missionIndex);
        return (
          <FactionMissionWaypoint
            key={`alt-${i}`}
            x={x}
            y={y}
            state={state}
            missionIndex={missionIndex}
            factionName={factionName}
            isEasyMission={isEasy}
            waypointEditMode={waypointEditMode || branchDevEdit}
            onAvailableMissionClick={onAvailableMissionClick}
            missionClickOptions={missionClickOptions}
          />
        );
      })}
      {branchDevEdit && typeof branch.onWaypointAuthoringChange === 'function'
        ? altPts.map(([x, y], i) => (
            <circle
              key={`alt-dev-handle-${i}`}
              className="explore-unknown-poi-dev-handle"
              cx={x}
              cy={y}
              r="24"
              fill="rgba(255, 60, 255, 0.2)"
              stroke="rgba(255, 255, 255, 0.95)"
              strokeWidth="2"
              style={{
                cursor: draggingBranchIndex === i ? 'grabbing' : 'grab',
                pointerEvents: 'auto',
                touchAction: 'none',
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
                  e.currentTarget.setPointerCapture(e.pointerId);
                }
                setDraggingBranchIndex(i);
                emitBranchAuthoringFromClient(e.clientX, e.clientY, i);
              }}
            />
          ))
        : null}
      {forkEditMode ? (
        <circle
          className="explore-unknown-poi-dev-handle explore-unknown-poi-dev-handle--fork"
          cx={fork[0]}
          cy={fork[1]}
          r="24"
          fill="rgba(255, 200, 60, 0.35)"
          stroke="rgba(255, 255, 255, 0.95)"
          strokeWidth="2"
          style={{
            cursor: draggingFork ? 'grabbing' : 'grab',
            pointerEvents: 'auto',
            touchAction: 'none',
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
              e.currentTarget.setPointerCapture(e.pointerId);
            }
            setDraggingFork(true);
            emitForkAuthoringFromClient(e.clientX, e.clientY);
          }}
        />
      ) : null}
    </g>
  );
}

function FactionMissionWaypoint({
  x,
  y,
  state,
  missionIndex,
  factionName,
  isEasyMission = false,
  waypointEditMode,
  onAvailableMissionClick,
  missionClickOptions = null,
}) {
  const isAvailable = state === 'available';
  const isDone = state === 'done';
  const orbBackground = getExploreUnknownMapFactionOrbBackground(factionName);
  const iconPath = getExploreUnknownMapFactionIconPath(factionName);
  const half = FACTION_MISSION_ORB_SIZE / 2;

  const groupStyle = {
    pointerEvents: waypointEditMode || !isAvailable ? 'none' : 'auto',
    cursor: !waypointEditMode && isAvailable ? 'pointer' : 'default',
  };

  const handleClick =
    waypointEditMode || !isAvailable
      ? undefined
      : (e) => {
          onAvailableMissionClick(missionIndex, false, missionClickOptions);
          e.currentTarget.blur();
        };

  const handleKeyDown =
    waypointEditMode || !isAvailable
      ? undefined
      : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAvailableMissionClick(missionIndex, false, missionClickOptions);
          }
        };

  return (
    <g
      className={`explore-unknown-poi-faction-group explore-unknown-poi-faction-group--${state}`}
      role={isAvailable ? 'button' : undefined}
      tabIndex={isAvailable && !waypointEditMode ? 0 : -1}
      style={groupStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-hidden={!isAvailable}
    >
      <circle cx={x} cy={y} r={half + 6} fill="transparent" />
      {isAvailable ? (
        <circle
          className="explore-unknown-poi-faction-glow"
          cx={x}
          cy={y}
          r={half + 2}
          fill="none"
          stroke="rgba(255, 255, 255, 0.55)"
          strokeWidth="2"
          aria-hidden
        />
      ) : null}
      <foreignObject
        x={x - half}
        y={y - half}
        width={FACTION_MISSION_ORB_SIZE}
        height={FACTION_MISSION_ORB_SIZE}
        className="explore-unknown-poi-faction-orb-host"
        aria-hidden
      >
        <div xmlns="http://www.w3.org/1999/xhtml" className="explore-unknown-poi-faction-orb-wrap">
          <div
            className="explore-unknown-poi-faction-orb"
            style={{ background: orbBackground }}
          >
            {iconPath ? (
              <img
                src={iconPath}
                alt=""
                className="explore-unknown-poi-faction-orb-icon"
                draggable={false}
                aria-hidden="true"
              />
            ) : null}
          </div>
          {isEasyMission ? (
            <img
              src="/images/pacifier.png"
              alt=""
              className="explore-unknown-poi-mission-pacifier explore-unknown-poi-faction-orb-pacifier"
              draggable={false}
              aria-hidden="true"
            />
          ) : null}
        </div>
      </foreignObject>
      {isDone ? (
        <g className="explore-unknown-poi-faction-done-tick" aria-hidden>
          <circle
            cx={x + half - 4}
            cy={y + half - 4}
            r="8"
            fill="rgba(10, 36, 22, 0.95)"
            stroke="rgba(74, 222, 128, 0.95)"
            strokeWidth="1.5"
          />
          <path
            d={`M ${x + half - 8} ${y + half - 4} L ${x + half - 5} ${y + half - 0.5} L ${x + half} ${y + half - 7.5}`}
            fill="none"
            stroke="#4ade80"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ) : null}
    </g>
  );
}

/**
 * Boss waypoint marker: red warning triangle with a black "DANGER" label on a yellow plate above it.
 * Replaces the regular circle for missions listed in `bossMissionIndices` (e.g. mission 10, the cave boss).
 *
 * Geometry (authoring px around centre `(x, y)`):
 *  - Triangle: equilateral pointing up, vertices at (x, y - 14), (x - 22, y + 24), (x + 22, y + 24).
 *  - Yellow "DANGER" plate: rounded rect centred at (x, y - 25), 56×14 px.
 *
 * State semantics mirror the regular circle marker:
 *  - `available`    – clickable, full red/yellow, hover/focus scale + glow ring.
 *  - `done`         – red/yellow at reduced opacity, with green tick overlay.
 *  - `unavailable`  – greyed out, not clickable.
 */
function BossMissionWaypoint({
  x,
  y,
  state,
  missionIndex,
  waypointEditMode,
  onAvailableMissionClick,
  missionClickOptions = null,
  showPacifier = false,
}) {
  const { t } = useTranslation();
  const dangerLabel = t('exploreUnknownPage.bossWaypointDangerLabel');
  const ariaLabel = t('exploreUnknownPage.bossWaypointAriaLabel', { missionIndex });

  const isAvailable = state === 'available';
  const isDone = state === 'done';
  const isUnavailable = state === 'unavailable';

  const triangleFill = isUnavailable ? '#3a3a3f' : '#dc2626';
  const triangleStroke = isUnavailable ? 'rgba(180, 180, 190, 0.9)' : '#000';
  const exclamationFill = isUnavailable ? 'rgba(220, 220, 230, 0.85)' : '#fff';
  const plateFill = isUnavailable ? '#5a5a60' : '#facc15';
  const plateStroke = isUnavailable ? 'rgba(180, 180, 190, 0.9)' : '#000';
  const plateText = isUnavailable ? 'rgba(230, 230, 235, 0.95)' : '#000';
  const groupOpacity = isDone ? 0.6 : 1;

  const trianglePoints = `${x},${y - 14} ${x - 22},${y + 24} ${x + 22},${y + 24}`;

  const groupStyle = {
    pointerEvents: waypointEditMode || !isAvailable ? 'none' : 'auto',
    cursor: !waypointEditMode && isAvailable ? 'pointer' : 'default',
  };

  const dimmedMarkerStyle = isDone || isUnavailable ? { opacity: groupOpacity } : undefined;

  const handleClick =
    waypointEditMode || !isAvailable
      ? undefined
      : (e) => {
          onAvailableMissionClick(missionIndex, false, missionClickOptions);
          e.currentTarget.blur();
        };

  const handleKeyDown =
    waypointEditMode || !isAvailable
      ? undefined
      : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAvailableMissionClick(missionIndex, false, missionClickOptions);
          }
        };

  return (
    <g
      className={`explore-unknown-poi-boss-group explore-unknown-poi-boss-group--${state}`}
      role={isAvailable ? 'button' : undefined}
      tabIndex={isAvailable && !waypointEditMode ? 0 : -1}
      style={groupStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={isAvailable ? ariaLabel : undefined}
    >
      {/* Generous transparent click target spanning triangle + plate. */}
      <rect x={x - 30} y={y - 34} width="60" height="62" fill="transparent" />

      <g style={dimmedMarkerStyle}>
        {/* Glow halo behind the boss marker (visible on hover/focus for available). */}
        {isAvailable ? (
          <polygon
            className="explore-unknown-poi-boss-glow"
            points={trianglePoints}
            fill="none"
            stroke="rgba(255, 80, 80, 0.65)"
            strokeWidth="3"
            strokeLinejoin="round"
            aria-hidden
          />
        ) : null}

        {/* Red warning triangle with black border. */}
        <polygon
          className="explore-unknown-poi-boss-triangle"
          points={trianglePoints}
          fill={triangleFill}
          stroke={triangleStroke}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />

        {/* Exclamation mark inside the triangle. */}
        <text
          x={x}
          y={y + 16}
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
          fontSize="22"
          fontWeight="900"
          fill={exclamationFill}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          !
        </text>

        {/* Yellow "DANGER" plate above the triangle. */}
        <rect
          className="explore-unknown-poi-boss-plate"
          x={x - 28}
          y={y - 32}
          width="56"
          height="14"
          rx="2.5"
          ry="2.5"
          fill={plateFill}
          stroke={plateStroke}
          strokeWidth="1.25"
        />
        <text
          x={x}
          y={y - 21}
          textAnchor="middle"
          fontFamily="Arial Black, Arial, sans-serif"
          fontSize="10"
          fontWeight="900"
          letterSpacing="0.6"
          fill={plateText}
          textLength="48"
          lengthAdjust="spacingAndGlyphs"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {dangerLabel}
        </text>
      </g>

      {showPacifier ? <MissionPacifierBadge x={x} y={y} /> : null}

      {/* Done tick overlay sits in the lower-right of the triangle. */}
      {isDone ? (
        <g aria-hidden>
          <circle
            cx={x + 14}
            cy={y + 18}
            r="8"
            fill="rgba(10, 36, 22, 0.95)"
            stroke="rgba(74, 222, 128, 0.95)"
            strokeWidth="1.5"
          />
          <path
            d={`M ${x + 10} ${y + 18} L ${x + 13} ${y + 21.5} L ${x + 18} ${y + 14.5}`}
            fill="none"
            stroke="#4ade80"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ) : null}
    </g>
  );
}

export default ExploreUnknownArrivalPointsOverlay;
